import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Page context definitions
 * These provide AI-friendly descriptions of each page without exposing sensitive data
 * Contexts are role-aware: employee, employer_hr, admin, sub_admin
 */
const PAGE_CONTEXTS = {
  // ============ ADMIN PAGES ============
  AdminDashboard: {
    title: 'Admin Dashboard',
    description: 'The administrator is viewing the system dashboard with overview of all employers, employees, and system-wide metrics.',
    capabilities: ['View system metrics', 'Monitor all employers', 'Access admin functions', 'View system health'],
    helpTopics: ['system administration', 'employer management', 'system metrics', 'admin tools'],
    roles: ['admin', 'sub_admin']
  },
  EmployeeManagement: {
    title: 'Employee Management',
    description: 'The administrator is managing employee records across all employers in the system.',
    capabilities: ['View all employees', 'Edit employee data', 'Manage employee accounts', 'Import/export employee data'],
    helpTopics: ['employee administration', 'account management', 'employee records', 'bulk operations'],
    roles: ['admin', 'sub_admin']
  },
  BenefitsManagement: {
    title: 'Benefits Management',
    description: 'The administrator is managing benefit products and plans available to employers.',
    capabilities: ['Create benefit products', 'Edit benefit details', 'Set pricing', 'Manage categories', 'Configure rating tables'],
    helpTopics: ['benefit products', 'plan configuration', 'pricing management', 'product catalog'],
    roles: ['admin', 'sub_admin']
  },
  Categories: {
    title: 'Benefit Categories',
    description: 'The administrator is managing benefit categories and their organization.',
    capabilities: ['Create categories', 'Edit category names', 'Organize products', 'Set category display order'],
    helpTopics: ['category management', 'product organization', 'benefit types'],
    roles: ['admin', 'sub_admin']
  },
  Employers: {
    title: 'Employer Management',
    description: 'The administrator is managing employer accounts and configurations.',
    capabilities: ['View all employers', 'Create employer accounts', 'Edit employer settings', 'Configure employer benefits'],
    helpTopics: ['employer accounts', 'company management', 'employer configuration', 'account setup'],
    roles: ['admin', 'sub_admin']
  },
  EnrollmentReports: {
    title: 'Enrollment Reports',
    description: 'The administrator is viewing system-wide enrollment reports and analytics.',
    capabilities: ['View enrollment data', 'Generate reports', 'Export data', 'Filter by employer'],
    helpTopics: ['enrollment analytics', 'reporting', 'data export', 'enrollment metrics'],
    roles: ['admin', 'sub_admin']
  },
  AuditLog: {
    title: 'Audit Log',
    description: 'The administrator is reviewing system audit logs and user activity.',
    capabilities: ['View system logs', 'Filter activities', 'Track changes', 'Monitor security events'],
    helpTopics: ['audit trails', 'system security', 'activity monitoring', 'compliance'],
    roles: ['admin', 'sub_admin']
  },
  Analytics: {
    title: 'System Analytics',
    description: 'The administrator is viewing comprehensive system analytics and insights.',
    capabilities: ['View analytics dashboards', 'Track KPIs', 'Generate insights', 'Export analytics'],
    helpTopics: ['system analytics', 'performance metrics', 'business intelligence', 'data insights'],
    roles: ['admin', 'sub_admin']
  },
  SubAdminManagement: {
    title: 'Sub-Admin Management',
    description: 'The administrator is managing sub-administrator accounts and permissions.',
    capabilities: ['Create sub-admins', 'Edit permissions', 'Manage access', 'View sub-admin activity'],
    helpTopics: ['sub-admin accounts', 'permission management', 'access control', 'admin roles'],
    roles: ['admin']
  },
  AdminSettings: {
    title: 'Admin Settings',
    description: 'The administrator is configuring system-wide settings and preferences.',
    capabilities: ['Configure system settings', 'Manage integrations', 'Set global preferences', 'Update system configuration'],
    helpTopics: ['system configuration', 'settings management', 'global preferences', 'system setup'],
    roles: ['admin', 'sub_admin']
  },
  SystemConfiguration: {
    title: 'System Configuration',
    description: 'The administrator is managing advanced system configuration and technical settings.',
    capabilities: ['Configure system parameters', 'Set API keys', 'Manage integrations', 'Update technical settings'],
    helpTopics: ['system config', 'API configuration', 'technical settings', 'integration setup'],
    roles: ['admin']
  },
  TaxRateManagement: {
    title: 'Tax Rate Management',
    description: 'The administrator is managing tax rates and configurations for benefit calculations.',
    capabilities: ['Set tax rates', 'Configure tax rules', 'Update federal/state rates', 'Manage tax tables'],
    helpTopics: ['tax rates', 'tax configuration', 'Section 125', 'tax rules'],
    roles: ['admin', 'sub_admin']
  },
  RatingTables: {
    title: 'Rating Tables',
    description: 'The administrator is managing rating tables used for benefit pricing calculations.',
    capabilities: ['Create rating tables', 'Edit rate structures', 'Apply to products', 'Configure pricing rules'],
    helpTopics: ['rating tables', 'pricing configuration', 'rate structures', 'benefit pricing'],
    roles: ['admin', 'sub_admin']
  },
  EmailTemplates: {
    title: 'Email Templates',
    description: 'The administrator is managing email templates used throughout the system.',
    capabilities: ['Create email templates', 'Edit template content', 'Manage variables', 'Test email sending'],
    helpTopics: ['email templates', 'communication', 'template variables', 'email configuration'],
    roles: ['admin', 'sub_admin']
  },
  CensusUpload: {
    title: 'Census Upload',
    description: 'The administrator is uploading employee census data for an employer.',
    capabilities: ['Upload census files', 'Map data fields', 'Validate data', 'Import employees'],
    helpTopics: ['census upload', 'data import', 'employee bulk import', 'file format'],
    roles: ['admin', 'sub_admin', 'employer_hr']
  },
  ExportCensus: {
    title: 'Census Export',
    description: 'The administrator is exporting census data for reporting or backup purposes.',
    capabilities: ['Export census data', 'Select fields', 'Download files', 'Generate reports'],
    helpTopics: ['data export', 'census download', 'employee data export', 'reporting'],
    roles: ['admin', 'sub_admin', 'employer_hr']
  },
  SetupTestUsers: {
    title: 'Test User Setup',
    description: 'The administrator is setting up test users for system testing.',
    capabilities: ['Create test users', 'Configure test scenarios', 'Reset test data', 'Manage test accounts'],
    helpTopics: ['test users', 'testing setup', 'demo accounts', 'test data'],
    roles: ['admin']
  },
  ClaimsManagement: {
    title: 'Claims Management',
    description: 'The administrator or HR manager is reviewing and managing benefit claims.',
    capabilities: ['View all claims', 'Approve/deny claims', 'Process reimbursements', 'Export claim data'],
    helpTopics: ['claims administration', 'claim processing', 'reimbursements', 'claim status'],
    roles: ['admin', 'sub_admin', 'employer_hr']
  },
  
  // ============ HR / EMPLOYER PAGES ============
  EmployerDashboard: {
    title: 'Employer Dashboard',
    description: 'The HR manager is viewing their company dashboard with employee enrollment metrics and pending actions.',
    capabilities: ['View company metrics', 'Monitor employee enrollments', 'Access HR tools', 'Manage company benefits'],
    helpTopics: ['company dashboard', 'enrollment metrics', 'HR management', 'employee overview'],
    roles: ['employer_hr', 'admin', 'sub_admin']
  },
  EmployerEmployees: {
    title: 'Company Employees',
    description: 'The HR manager is managing their company\'s employee roster.',
    capabilities: ['View employees', 'Add new employees', 'Edit employee information', 'Manage employee status'],
    helpTopics: ['employee management', 'employee roster', 'employee data', 'employee onboarding'],
    roles: ['employer_hr', 'admin', 'sub_admin']
  },
  EmployerEnrollments: {
    title: 'Company Enrollments',
    description: 'The HR manager is viewing and managing employee benefit enrollments.',
    capabilities: ['View all enrollments', 'Monitor enrollment status', 'Assist with enrollments', 'Generate enrollment reports'],
    helpTopics: ['enrollment management', 'employee enrollments', 'enrollment status', 'enrollment assistance'],
    roles: ['employer_hr', 'admin', 'sub_admin']
  },
  CompanySettings: {
    title: 'Company Settings',
    description: 'The HR manager is configuring their company settings and preferences.',
    capabilities: ['Update company info', 'Configure benefit options', 'Set enrollment periods', 'Manage HR preferences'],
    helpTopics: ['company configuration', 'benefit setup', 'company preferences', 'enrollment setup'],
    roles: ['employer_hr', 'admin', 'sub_admin']
  },
  HRManagers: {
    title: 'HR Manager Accounts',
    description: 'The administrator or HR admin is managing HR manager accounts for their company.',
    capabilities: ['Add HR managers', 'Edit HR accounts', 'Manage permissions', 'Remove HR access'],
    helpTopics: ['HR accounts', 'user management', 'HR permissions', 'account administration'],
    roles: ['employer_hr', 'admin', 'sub_admin']
  },
  EnrollmentPeriods: {
    title: 'Enrollment Periods',
    description: 'The HR manager is managing open enrollment and enrollment period configurations.',
    capabilities: ['Create enrollment periods', 'Set enrollment dates', 'Configure eligibility', 'Manage enrollment windows'],
    helpTopics: ['open enrollment', 'enrollment periods', 'enrollment windows', 'eligibility dates'],
    roles: ['employer_hr', 'admin', 'sub_admin']
  },
  CompanyOnboarding: {
    title: 'Company Onboarding',
    description: 'The HR manager is completing their company onboarding process.',
    capabilities: ['Complete company profile', 'Set up benefits', 'Configure settings', 'Finish onboarding'],
    helpTopics: ['company setup', 'initial configuration', 'onboarding steps', 'getting started'],
    roles: ['employer_hr']
  },
  DemographicReports: {
    title: 'Demographic Reports',
    description: 'The HR manager is viewing demographic reports for their employees.',
    capabilities: ['View demographics', 'Generate reports', 'Export data', 'Analyze employee demographics'],
    helpTopics: ['demographic analytics', 'employee demographics', 'workforce analytics', 'reporting'],
    roles: ['employer_hr', 'admin', 'sub_admin']
  },
  TaxSavingsReports: {
    title: 'Tax Savings Reports',
    description: 'The HR manager is viewing tax savings reports and calculations.',
    capabilities: ['View tax savings', 'Calculate Section 125 savings', 'Generate tax reports', 'Export tax data'],
    helpTopics: ['tax savings', 'Section 125', 'pre-tax benefits', 'tax reports'],
    roles: ['employer_hr', 'admin', 'sub_admin']
  },
  PenetrationReports: {
    title: 'Benefit Penetration Reports',
    description: 'The HR manager is viewing benefit penetration and adoption metrics.',
    capabilities: ['View participation rates', 'Analyze benefit adoption', 'Generate penetration reports', 'Track enrollment trends'],
    helpTopics: ['benefit penetration', 'participation rates', 'enrollment analytics', 'adoption metrics'],
    roles: ['employer_hr', 'admin', 'sub_admin']
  },
  Invite: {
    title: 'Employee Invitations',
    description: 'The HR manager is inviting employees to enroll in benefits.',
    capabilities: ['Send employee invites', 'Generate invite codes', 'Track invitation status', 'Resend invitations'],
    helpTopics: ['employee invitations', 'enrollment invites', 'invite codes', 'employee onboarding'],
    roles: ['employer_hr', 'admin', 'sub_admin']
  },
  
  // ============ EMPLOYEE PAGES ============
  W4Form: {
    title: 'W-4 Tax Withholding Form',
    description: 'The employee is viewing the W-4 federal tax withholding form where they can configure their tax withholding preferences.',
    capabilities: ['View W-4 form', 'Update tax withholding settings', 'Download W-4 PDF'],
    helpTopics: ['tax withholding', 'W-4 form', 'federal tax', 'exemptions', 'allowances'],
    roles: ['employee']
  },
  BenefitsCatalog: {
    title: 'Benefits Catalog',
    description: 'The employee is browsing available benefit products and plans.',
    capabilities: ['View all benefits', 'Filter by category', 'Add benefits to cart', 'View benefit details'],
    helpTopics: ['benefit options', 'health insurance', 'dental', 'vision', 'life insurance', 'coverage options'],
    roles: ['employee']
  },
  BenefitDetail: {
    title: 'Benefit Detail',
    description: 'The employee is viewing detailed information about a specific benefit product.',
    capabilities: ['View coverage details', 'See pricing', 'Add to enrollment cart', 'View FAQs'],
    helpTopics: ['coverage details', 'pricing', 'enrollment', 'benefit specifics'],
    roles: ['employee']
  },
  EnrollmentReview: {
    title: 'Enrollment Review',
    description: 'The employee is reviewing their benefit selections before finalizing enrollment.',
    capabilities: ['Review selected benefits', 'See total costs', 'Modify selections', 'Proceed to signing'],
    helpTopics: ['review enrollment', 'total cost', 'premium calculations', 'finalize enrollment'],
    roles: ['employee']
  },
  EnrollmentSign: {
    title: 'Enrollment Signature',
    description: 'The employee is signing and submitting their benefit enrollment.',
    capabilities: ['Sign enrollment', 'Submit enrollment', 'Review terms'],
    helpTopics: ['electronic signature', 'enrollment submission', 'legal agreements'],
    roles: ['employee']
  },
  EnrollmentIntro: {
    title: 'Enrollment Introduction',
    description: 'The employee is viewing an introduction to the benefits enrollment process.',
    capabilities: ['Start enrollment', 'Learn about benefits', 'Watch intro video'],
    helpTopics: ['getting started', 'enrollment process', 'benefit overview'],
    roles: ['employee']
  },
  MyBenefits: {
    title: 'My Benefits',
    description: 'The employee is viewing their current and past benefit enrollments.',
    capabilities: ['View active benefits', 'Download benefit summaries', 'Check enrollment status'],
    helpTopics: ['current benefits', 'enrollment status', 'benefit documents'],
    roles: ['employee']
  },
  EmployeeDashboard: {
    title: 'Employee Dashboard',
    description: 'The employee is on their dashboard with overview of benefits and actions.',
    capabilities: ['View dashboard', 'Quick enrollment access', 'See notifications'],
    helpTopics: ['dashboard overview', 'quick actions', 'benefit status'],
    roles: ['employee']
  },
  EmployeeProfile: {
    title: 'Employee Profile',
    description: 'The employee is viewing or editing their profile information.',
    capabilities: ['Update personal information', 'Edit contact details', 'Manage preferences'],
    helpTopics: ['profile information', 'contact details', 'personal data'],
    roles: ['employee']
  },
  FamilyMembers: {
    title: 'Family Members',
    description: 'The employee is managing family member information for dependent coverage.',
    capabilities: ['Add family members', 'Edit dependent info', 'Remove dependents'],
    helpTopics: ['dependents', 'family coverage', 'spouse', 'children'],
    roles: ['employee']
  },
  Help: {
    title: 'Help Center',
    description: 'The user is viewing the help center with FAQs and support options.',
    capabilities: ['Browse FAQs', 'Contact support', 'Find answers'],
    helpTopics: ['help', 'support', 'frequently asked questions', 'contact information'],
    roles: ['employee', 'employer_hr', 'admin', 'sub_admin']
  },
  QualifyingLifeEvents: {
    title: 'Qualifying Life Events',
    description: 'The employee is viewing or submitting a qualifying life event for special enrollment.',
    capabilities: ['Submit QLE', 'View QLE status', 'Upload documentation'],
    helpTopics: ['qualifying life events', 'special enrollment', 'life changes', 'marriage', 'birth'],
    roles: ['employee']
  },
  Claims: {
    title: 'My Claims',
    description: 'The employee is viewing or submitting their benefit claims.',
    capabilities: ['Submit claims', 'View claim status', 'Upload receipts'],
    helpTopics: ['filing claims', 'claim status', 'reimbursement'],
    roles: ['employee']
  },
  ClaimDetail: {
    title: 'Claim Details',
    description: 'The user is viewing detailed information about a specific claim.',
    capabilities: ['View claim details', 'Check status', 'Download receipts', 'View processing history'],
    helpTopics: ['claim information', 'claim status', 'reimbursement details'],
    roles: ['employee', 'employer_hr', 'admin', 'sub_admin']
  },
  FlexOptimization: {
    title: 'Premium Benefit Optimizer',
    description: 'The employee is using the premium benefit optimization tool to calculate optimal benefit selections.',
    capabilities: ['Optimize premium benefits', 'See tax savings', 'Calculate options'],
    helpTopics: ['premium benefits', 'Section 125', 'tax optimization', 'benefit calculator'],
    roles: ['employee']
  },
  PayrollReports: {
    title: 'Payroll Reports',
    description: 'The user is viewing payroll and benefit deduction reports.',
    capabilities: ['View payroll reports', 'Download reports', 'See deductions'],
    helpTopics: ['payroll', 'deductions', 'pay stubs', 'benefit costs'],
    roles: ['employee', 'employer_hr', 'admin', 'sub_admin']
  },
  Enrollment: {
    title: 'Benefit Enrollment',
    description: 'The employee is in the benefit enrollment workflow.',
    capabilities: ['Select benefits', 'Review options', 'Add to cart', 'Continue enrollment'],
    helpTopics: ['benefit enrollment', 'plan selection', 'coverage options', 'enrollment process'],
    roles: ['employee']
  },
  EmployeeOnboarding: {
    title: 'Employee Onboarding',
    description: 'The employee is completing their initial onboarding process.',
    capabilities: ['Complete profile', 'Set up account', 'Review company info', 'Finish onboarding'],
    helpTopics: ['getting started', 'account setup', 'onboarding steps', 'profile completion'],
    roles: ['employee']
  },
  TwoFactorAuth: {
    title: 'Two-Factor Authentication',
    description: 'The user is managing their two-factor authentication settings.',
    capabilities: ['Enable 2FA', 'Disable 2FA', 'View backup codes', 'Configure authenticator'],
    helpTopics: ['security', 'two-factor authentication', '2FA', 'account security'],
    roles: ['employee', 'employer_hr', 'admin', 'sub_admin']
  }
};

/**
 * Hook to track and provide page context for the AI assistant
 * @param {object} user - Current user object with role information
 * @returns {object} Current page context information
 */
export function usePageContext(user = null) {
  const location = useLocation();
  const [pageContext, setPageContext] = useState(null);

  useEffect(() => {
    const updateContext = async () => {
      // Extract page name from path
      const pathname = location.pathname;
      const searchParams = new URLSearchParams(location.search);
      let pageName = 'Unknown';
      
      if (pathname === '/' || pathname === '') {
        pageName = 'EmployeeDashboard';
      } else {
        // Remove leading slash and get the first segment
        const pathSegment = pathname.replace(/^\//, '').split('/')[0];
        
        // Find matching page name (case-insensitive)
        const matchedKey = Object.keys(PAGE_CONTEXTS).find(
          key => key.toLowerCase() === pathSegment.toLowerCase()
        );
        
        pageName = matchedKey || pathSegment;
      }

      // Get base context
      let context = PAGE_CONTEXTS[pageName] || {
        title: pageName,
        description: `The user is on the ${pageName} page.`,
        capabilities: [],
        helpTopics: [],
        roles: ['employee', 'employer_hr', 'admin', 'sub_admin']
      };
      
      // Check if user has access to this page based on role
      const userRole = user?.user_role || user?.role || 'employee';
      const hasAccess = !context.roles || context.roles.includes(userRole);
      
      if (!hasAccess) {
        // If user doesn't have access to this page context, provide generic context
        context = {
          title: pageName,
          description: `Viewing ${pageName} page.`,
          capabilities: [],
          helpTopics: [],
          roles: [userRole]
        };
      }

      // Enhance context for BenefitDetail pages
      if (pageName === 'BenefitDetail' && searchParams.get('id')) {
        try {
          const { api } = await import('@/api/apiClient');
          const productId = searchParams.get('id');
          const products = await api.entities.BenefitProduct.filter({ id: productId });
          
          if (products.length > 0) {
            const product = products[0];
            const categoryName = product.category_name || 'Benefit';
            const productName = product.name || 'Product';
            
            context = {
              ...context,
              title: `${productName} (${categoryName})`,
              description: `The user is viewing detailed information about ${productName}, a ${categoryName.toLowerCase()} benefit.`,
              capabilities: [
                'View coverage details',
                'See pricing',
                'Add to enrollment cart',
                'View FAQs',
                'Watch benefit video'
              ],
              helpTopics: [
                'coverage details',
                'pricing',
                'enrollment',
                categoryName.toLowerCase(),
                productName.toLowerCase()
              ]
            };
          }
        } catch (err) {
          console.error('Failed to load benefit details for context:', err);
        }
      }

      // Enhance context for BenefitsCatalog with category filter
      if (pageName === 'BenefitsCatalog' && searchParams.get('category')) {
        const categoryName = searchParams.get('category');
        context = {
          ...context,
          title: `${categoryName} Benefits`,
          description: `The user is browsing ${categoryName.toLowerCase()} benefit products.`,
          capabilities: [
            `View all ${categoryName.toLowerCase()} benefits`,
            'Filter by category',
            'Add benefits to cart',
            'Compare options'
          ],
          helpTopics: [
            'benefit options',
            categoryName.toLowerCase(),
            'coverage',
            'pricing'
          ]
        };
      }

      setPageContext({
        pageName,
        path: pathname,
        userRole,
        ...context,
        timestamp: new Date().toISOString()
      });
    };

    updateContext();
  }, [location, user]);

  return pageContext;
}

/**
 * Generate a safe context message for the AI without sensitive data
 * @param {object} pageContext - Page context from usePageContext
 * @param {object} additionalContext - Any additional non-sensitive context
 * @returns {string} Formatted context message for AI
 */
export function generateAIContextMessage(pageContext, additionalContext = {}) {
  if (!pageContext) return '';

  const parts = [
    `User is now on: ${pageContext.title}`
  ];
  
  // Add role-specific description
  if (pageContext.userRole) {
    const roleLabels = {
      'admin': 'System Administrator',
      'sub_admin': 'Sub-Administrator',
      'employer_hr': 'HR Manager',
      'employee': 'Employee'
    };
    const roleLabel = roleLabels[pageContext.userRole] || pageContext.userRole;
    parts.push(`User role: ${roleLabel}`);
  }
  
  parts.push(pageContext.description);

  if (pageContext.capabilities && pageContext.capabilities.length > 0) {
    parts.push(`Available actions: ${pageContext.capabilities.join(', ')}`);
  }

  if (additionalContext.itemCount) {
    parts.push(`${additionalContext.itemCount} items in enrollment cart`);
  }

  if (additionalContext.selectedCategory) {
    parts.push(`Viewing category: ${additionalContext.selectedCategory}`);
  }

  if (additionalContext.enrollmentStatus) {
    parts.push(`Enrollment status: ${additionalContext.enrollmentStatus}`);
  }

  return parts.join('. ') + '.';
}

export default usePageContext;
